import * as React from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogBodyProps } from '@fluentui/react-dialog'
import useAxios from '../../../../hooks/useAxios'
import { Button, DialogActions, DialogBody, DialogContent, DialogTitle, DialogTrigger } from '@fluentui/react-components'
import FormContainer from '../../../../components/Form/FormContainer'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { Destination } from '../../../../context/AxiosContext'
import { formatInputFolderName } from '../../../../helpers/regexHelper'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { AxiosResponse } from 'axios'
import DocumentCombobox, { DocumentComboboxProps } from '../../Combobox/DocumentCombobox'
import { DocumentSelectionType } from '../../../../components/Dropdown/DocumentDropdown'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import { OfficeDocumentDialogBodyValues } from './OfficeDocumentDialogBody'
import { FolderDriveItem } from '../../../../components/Document/DocumentFlatTree'
import FormikInputField from '../../../../components/Formik/FormikInputField'
import { useFormikContext } from 'formik'
import { MutateDocumentDialogBodyValues } from './MutateDocumentDialogBody'
import { HammockFormDialogProps } from '../HammockFormDialog'
import { FormikConfig } from 'formik/dist/types'
import * as Yup from 'yup'

export const getCreateRootFolderDialogBodySchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        name: Yup.string()
            .required(translate('file.pivot.document.create.name.required'))
            .test('is-valid-name', translate('file.pivot.document.create.name.invalid'), value => !value.match(/^\./)),
    })
}

export const getCreateSubFolderDialogBodySchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        folderId: Yup.string().required(translate('file.pivot.document.create.folderid.required')),
        name: Yup.string()
            .required(translate('file.pivot.document.create.name.required'))
            .test('is-valid-name', translate('file.pivot.document.create.name.invalid'), value => !value.match(/^\./)),
    })
}

export type CreateFolderDialogBodyProps = DialogBodyProps & {
    isRoot: boolean
    folders: FolderDriveItem[]
    parentId?: string
    parent?: DriveItem
    callback?: () => void
}

export type CreateFolderDialogBodyValues = {
    name: string
    folderId: string
}

export default function CreateFolderDialogBody(props: HammockFormDialogProps<CreateFolderDialogBodyValues, CreateFolderDialogBodyProps>) {
    const axios = useAxios()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const { setFieldValue, isSubmitting } = useFormikContext<MutateDocumentDialogBodyValues>()

    const dialogFormBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as CreateFolderDialogBodyProps), [props])
    const viewportSmall = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    const folders = useMemo(() => dialogFormBodyProps?.folders?.filter(item => item.itemType === 'branch'), [dialogFormBodyProps])

    const title = translate(dialogFormBodyProps?.isRoot ? 'file.pivot.document.create.folder.root' : 'file.pivot.document.create.folder.sub')

    const handleSubmit = useCallback<FormikConfig<CreateFolderDialogBodyValues>['onSubmit']>(values => {
        return axios
            .post<
                DriveItem,
                AxiosResponse<DriveItem>,
                any
            >(`/items/${dialogFormBodyProps.isRoot ? dialogFormBodyProps.parentId : values.folderId}/children`, { folder: {}, name: values.name }, { baseURL: Destination.Documents, headers: { SuppressErrors: 'true' } })
            .then(() => dialogFormBodyProps.callback?.())
    }, [])

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    return (
        <DialogBody>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <FormContainer>
                    {!dialogFormBodyProps?.isRoot && (
                        <FormikComboboxField<OfficeDocumentDialogBodyValues, DocumentComboboxProps>
                            required
                            labelKey={'file.pivot.document.create.folder.parent'}
                            orientation={viewportSmall ? 'vertical' : 'horizontal'}
                            fieldName={'folderId'}
                            component={DocumentCombobox}
                            componentProps={{
                                selectionType: DocumentSelectionType.FolderOnly,
                                entities: folders,
                                tabIndex: -1,
                            }}
                        />
                    )}

                    <FormikInputField
                        required
                        labelKey={'file.pivot.document.create.folder.name'}
                        orientation={viewportSmall ? 'vertical' : 'horizontal'}
                        fieldName={'name'}
                        componentProps={{
                            placeholder: translate('file.pivot.document.mutate.form.name'),
                            onBlur: e => setFieldValue('name', formatInputFolderName(e.currentTarget.value)),
                            onKeyDown: e => {
                                if (e.key === 'Enter') {
                                    setFieldValue('name', formatInputFolderName(e.currentTarget.value))
                                }
                            },
                        }}
                    />
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <Button appearance="primary" type={'submit'} disabled={isSubmitting}>
                    {translate('file.pivot.document.create.folder.submit')}
                </Button>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary" disabled={isSubmitting}>
                        {translate('action.cancel')}
                    </Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}
